import * as React from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Input, TextArea } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { useState } from "react";
import { toast } from "react-toastify";
import { ContainerCard } from "components/MatterDetails/ContactCard";
import { Tile } from "components/UIComponents/Tile";
import { IoIosAddCircleOutline } from "react-icons/io";
import { UserScreens } from "./AddUserScreens";
import styles from "./CaseFiles.module.scss";
import Document from "../../assets/fileIcons/Document.svg";
import SubDocument from "../../assets/subdocument.png";
import Interview from "../../assets/CaseFileIcons/ClientInterview.svg";
import EditIcon from "../../assets/common/editBoxIcon.png";
import { SvgIcon } from "@progress/kendo-react-common";
import UserScreenIcon from "../../assets/CaseFileIcons/UserScreen.svg";
import { reorderIcon } from "@progress/kendo-svg-icons";
import { MdCancel } from "react-icons/md";
import AddEditDocTemplate from "./AddEditDocTemplate";
import ScreenDesigner from "pages/CaseFile/CaseWorkFlow/ScreenDesigner/ScreenDesigner";
import AddEditClientInterview from "./AddEditClientInterview";
import { DocumentTemplates } from "./AddDocumentTemplate";
import { ClientInterviews } from "./ClientInterviews";
import {
  DragAndDrop,
  useDraggable,
  useDroppable,
} from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import ReorderContext from "components/CaseFiles/ReorderContext";

const DraggableWorkflowItem = ({ children, item, onRemove, editItem }) => {
  const [dragged, setDragged] = useState(false);
  const [dropped, setDropped] = useState(false);
  const [direction, setDirection] = useState(null);
  const [initial, setInitial] = useState({ x: 0, y: 0 });
  const { dragStart, reorder } = React.useContext(ReorderContext);
  const element = React.useRef(null);

  const handlePress = (event) => {
    setInitial({
      x: event.clientX - event.offsetX,
      y: event.clientY - event.offsetY,
    });
  };

  const handleDragStart = (event) => {
    if (
      !event.originalEvent.target ||
      !event.originalEvent.target.dataset.dragHandle
    ) {
      return;
    }
    setDragged(true);
    dragStart(item);
  };

  const handleDrag = (event) => {
    if (!element.current || !dragged) {
      return;
    }
    element.current.style.transform = `translateY(${
      event.clientY - initial.y + event.scrollY
    }px)`;
  };

  const handleDragEnd = () => {
    setDragged(false);
    setDropped(false);
    setInitial({ x: 0, y: 0 });
    if (element.current) {
      element.current.style.transform = null;
    }
  };

  const handleDragEnter = () => {
    setDropped(true);
    setDirection(null);
  };

  const handleDragOver = (event) => {
    if (!element.current) {
      return;
    }
    const rect = element.current.getBoundingClientRect();
    setDirection(
      rect.top + rect.height / 2 <= event.pageY ? "after" : "before"
    );
  };

  const handleDragLeave = () => {
    setDropped(false);
    setDirection(null);
  };

  const handleDrop = () => {
    reorder(item, direction);
    setDropped(false);
    setDirection(null);
  };

  useDraggable(
    element,
    {
      onPress: handlePress,
      onDragStart: handleDragStart,
      onDrag: handleDrag,
      onDragEnd: handleDragEnd,
    },
    {
      autoScroll: dragged,
    }
  );

  useDroppable(element, {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  });

  return (
    <div
      ref={element}
      style={{
        backgroundColor: "#fff",
        userSelect: "none",
        pointerEvents: dragged ? "none" : undefined,
        opacity: dragged ? "0.8" : undefined,
      }}
    >
      {dropped && direction === "before" && (
        <div
          style={{
            outlineStyle: "solid",
            outlineWidth: 1,
            outlineColor: "red",
          }}
        />
      )}
      {children}
      {dropped && direction === "after" && (
        <div
          style={{
            outlineStyle: "solid",
            outlineWidth: 1,
            outlineColor: "red",
          }}
        />
      )}
    </div>
  );
};

const WorkFlowDialog = (props) => {
  const [folderName, setFolderName] = useState(
    props.isEdit ? props?.isEdit?.name : null
  );
  const [details, setDetails] = useState(
    props.isEdit ? props?.isEdit?.details : null
  );
  const [showScreens, setShowScreens] = useState(false);
  const [showDocTemplate, setShowDocTemplate] = useState(false);
  const [showClientInterview, setShowClientInterview] = useState(false);
  const [showSubDocument, setShowSubDocument] = useState(false);
  const [editScreen, setEditScreen] = useState();
  const [editDocTemplate, setEditDocTemplate] = useState();
  const [editClientInterview, setEditClientInterview] = useState();
  const [workFlowItems, setWorkFlowItems] = useState(
    props.isEdit ? [...props?.isEdit?.workflowItems] : []
  );
  const [activeItem, setActiveItem] = useState(null);

  const reorder = (dataItem, direction) => {
    if (activeItem === dataItem) {
      return;
    }
    let reorderedData = workFlowItems.slice();
    let prevIndex = reorderedData.findIndex((p) => p === activeItem);
    let nextIndex =
      reorderedData.findIndex((p) => p === dataItem) +
      (direction === "before" ? -1 : 0);
    if (prevIndex > nextIndex) {
      nextIndex++;
    }
    reorderedData.splice(prevIndex, 1);
    reorderedData.splice(nextIndex, 0, activeItem || reorderedData[0]);
    reorderedData = reorderedData.map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    setWorkFlowItems(reorderedData);
  };

  const dragStart = (dataItem) => {
    setActiveItem(dataItem);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = workFlowItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    try {
      if (props.isEdit) {
        props.editWorkFlow({
          ...props.isEdit,
          name: folderName,
          details: details,
          workflowItems: items,
        });
      } else {
        props.addWorkFlow({
          name: folderName,
          details: details,
          workflowItems: items,
        });
      }
    } catch (error) {
      console.log(error);
      toast.error("Unexpected Error Occured");
    }
  };

  const addScreens = (data = []) => {
    setWorkFlowItems([...workFlowItems, ...data]);
    setShowScreens(false);
  };

  const addDocTemplate = (data) => {
    const updatedData = data.map((template) => ({
      ...template,
      itemType: "Document",
      active: true,
    }));
    setWorkFlowItems([...workFlowItems, ...updatedData]);
    setShowDocTemplate(false);
  };

  const addSubDocument = (data) => {
    const updatedData = data.map((template) => ({
      ...template,
      itemType: "SubDocument",
      active: true,
    }));
    setWorkFlowItems([...updatedData, ...workFlowItems]);
    setShowSubDocument(false);
  };

  const addClientInterview = (data) => {
    const updatedData = data.map((template) => ({
      ...template,
      itemType: "Interview",
      active: true,
    }));
    setWorkFlowItems([...workFlowItems, ...updatedData]);
    setShowClientInterview(false);
  };
  return (
    <Dialog
      title={`${props?.isEdit ? "Edit" : "Add"} Workflow`}
      onClose={props.cancelEdit}
      width={700}
    >
      <form onSubmit={handleSubmit}>
        <div className="px-4 py-3">
          <ContainerCard
            className="p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto"
            height="160px"
          >
            <div className="mt-3">
              <Label className="label mb-1 w-100">Workflow Name&nbsp;</Label>
              <Input
                className="px-3 pt-1"
                style={{
                  width: "260px",
                  height: "32px",
                }}
                placeholder={"Add Folder"}
                name="folderName"
                required
                onChange={(e) => {
                  setFolderName(e.value);
                }}
                value={folderName}
                type="text"
              />
            </div>
            <div className="mt-2">
              <Label className="label mb-1 w-100">Details</Label>
              <TextArea
                style={{
                  width: "100%",
                  maxWidth: "none",
                  height: "32px",
                }}
                placeholder={"Enter Details"}
                id="details"
                name="details"
                className="px-2"
                onChange={(e) => {
                  setDetails(e.value);
                }}
                type="text"
                value={details}
                rows={1}
              />
            </div>
          </ContainerCard>

          <Tile
            heading="Workflow Items"
            className="px-4 py-2 mt-2 overflow-auto"
            headingItems={
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="button-small d-flex align-items-center bgsecondary text-black gap-1 px-2 py-0"
                  onClick={() => setShowScreens(true)}
                >
                  <IoIosAddCircleOutline size={16} /> New Screen
                </button>
                <button
                  type="button"
                  className="button-small d-flex align-items-center bgsecondary text-black gap-1 px-2 py-0"
                  onClick={() => setShowDocTemplate(true)}
                >
                  <IoIosAddCircleOutline size={16} /> Document Template
                </button>
                <Button
                  type="button"
                  className="button-small d-flex align-items-center bgsecondary text-black gap-1 px-2 py-0"
                  onClick={() => setShowSubDocument(true)}
                  disabled={
                    workFlowItems.filter(
                      (item) => item.itemType === "Document" && item.active
                    ).length === 0
                  }
                >
                  <IoIosAddCircleOutline size={16} /> Sub Document
                </Button>
              </div>
            }
            height="300px"
          >
            <div className="container">
              <ReorderContext.Provider
                value={{
                  reorder: reorder,
                  dragStart: dragStart,
                }}
              >
                <DragAndDrop>
                  {workFlowItems
                    ?.filter((screen) => screen.active)
                    .sort((a, b) => {
                      if (a.order == null) return -1;
                      if (b.order == null) return 1;
                      return a.order - b.order;
                    })
                    .map((item, index) => {
                      if (
                        item.itemType === "Document" ||
                        item.itemType === "SubDocument"
                      ) {
                        return (
                          <DraggableWorkflowItem
                            key={index}
                            item={item}
                            onRemove={(itemId) => {
                              setWorkFlowItems((prevItems) =>
                                [...prevItems].map((item) => {
                                  if (itemId === item.documentTemplateId) {
                                    return {
                                      ...item,
                                      active: false,
                                    };
                                  }
                                  return item;
                                })
                              );
                            }}
                            editItem={(item) => {
                              console.log(item);
                              setEditDocTemplate(item.documentTemplateId);
                            }}
                          >
                            <UserDocumentFlowItem
                              item={item}
                              key={index}
                              index={index}
                              onRemove={(itemId) => {
                                setWorkFlowItems((prevItems) =>
                                  [...prevItems].map((item) => {
                                    if (itemId === item.documentTemplateId) {
                                      return {
                                        ...item,
                                        active: false,
                                      };
                                    }
                                    return item;
                                  })
                                );
                              }}
                              editDocument={(item) => {
                                console.log(item);
                                setEditDocTemplate(item.documentTemplateId);
                                // setShowDocTemplate(true);
                              }}
                            />
                          </DraggableWorkflowItem>
                        );
                      }

                      if (item.itemType === "Interview") {
                        return (
                          <DraggableWorkflowItem
                            key={index}
                            item={item}
                            onRemove={(itemId) => {
                              setWorkFlowItems((prevItems) =>
                                [...prevItems].map((item) => {
                                  if (itemId === item.clientInterviewId) {
                                    return {
                                      ...item,
                                      active: false,
                                    };
                                  }
                                  return item;
                                })
                              );
                            }}
                            editItem={(item) => {
                              setEditClientInterview(item.clientInterviewId);
                            }}
                          >
                            <UserClientInterviewFlowItem
                              item={item}
                              key={index}
                              index={index}
                              onRemove={(itemId) => {
                                setWorkFlowItems((prevItems) =>
                                  [...prevItems].map((item) => {
                                    if (itemId === item.clientInterviewId) {
                                      return {
                                        ...item,
                                        active: false,
                                      };
                                    }
                                    return item;
                                  })
                                );
                              }}
                              editClientInterview={(item) => {
                                setEditClientInterview(item.clientInterviewId);
                              }}
                            />
                          </DraggableWorkflowItem>
                        );
                      } else {
                        return (
                          <DraggableWorkflowItem
                            key={index}
                            item={item}
                            onRemove={(itemId) => {
                              setWorkFlowItems((prevItems) =>
                                [...prevItems].map((item) => {
                                  if (itemId === item.userScreenId) {
                                    return {
                                      ...item,
                                      active: false,
                                    };
                                  }
                                  return item;
                                })
                              );
                            }}
                            editItem={(item) => {
                              setEditScreen(item);
                            }}
                          >
                            <UserScreenWorkFlowItem
                              item={item}
                              key={index}
                              index={index}
                              onRemove={(itemId) => {
                                setWorkFlowItems((prevItems) =>
                                  [...prevItems].map((item) => {
                                    if (itemId === item.userScreenId) {
                                      return {
                                        ...item,
                                        active: false,
                                      };
                                    }
                                    return item;
                                  })
                                );
                              }}
                              editDocument={(item) => {
                                setEditScreen(item);
                              }}
                            />
                          </DraggableWorkflowItem>
                        );
                      }
                    })}
                </DragAndDrop>
              </ReorderContext.Provider>
            </div>
            {/* <ReorderContext.Provider
              value={{
                reorder: reorder,
                dragStart: dragStart,
              }}
            >
              <DragAndDrop> */}

            {/* </DragAndDrop>
            </ReorderContext.Provider> */}
          </Tile>
        </div>
        <div className="dialog-footer">
          <Button
            className="common-btn me-4"
            disabled={
              !props.isEdit &&
              workFlowItems.filter((item) => item.active).length === 0
            }
          >
            Save
          </Button>
        </div>
      </form>

      {showScreens && (
        <UserScreens
          matterId={props.matterId}
          addedScreens={
            workFlowItems?.filter((screen) => screen.active === true)
            // : []
          }
          addScreens={addScreens}
          cancelEdit={() => setShowScreens(false)}
          screenType="UserScreen"
        />
      )}
      {showSubDocument && (
        <DocumentTemplates
          matterId={props.matterId}
          addedTemplates={workFlowItems?.filter(
            (screen) => screen.active === true && screen.itemType === "Document"
          )}
          addScreens={addSubDocument}
          cancelEdit={() => setShowSubDocument(false)}
          title={"Sub Document Templates"}
        />
      )}
      {showDocTemplate && (
        <DocumentTemplates
          matterId={props.matterId}
          title={"Document Templates"}
          addedTemplates={workFlowItems?.filter(
            (screen) => screen.active === true && screen.itemType === "Document"
          )}
          addScreens={addDocTemplate}
          cancelEdit={() => setShowDocTemplate(false)}
        />
      )}

      {showClientInterview && (
        <ClientInterviews
          cancelEdit={() => setShowClientInterview(false)}
          addedTemplates={workFlowItems?.filter(
            (screen) =>
              screen.active === true && screen.itemType === "Interview"
          )}
          addScreens={addClientInterview}
        />
      )}

      {editScreen && (
        <ScreenDesigner
          matterId={props.matterId}
          screenId={editScreen?.userScreenId}
          onClose={(isSuccess, data) => {
            if (isSuccess && data) {
              setWorkFlowItems((workFlowItem) =>
                workFlowItem.map((item) => {
                  if (item.userScreenId === data.userScreenId) {
                    return {
                      ...item,
                      name: data.name,
                    };
                  }
                  return item;
                })
              );
            }
            setEditScreen();
          }}
        />
      )}
      {editDocTemplate && (
        <AddEditDocTemplate
          matterId={props.matterId}
          cancelEdit={() => {
            setEditDocTemplate();
          }}
          updateTemplates={(data) => {
            const id = "documentTemplateId";
            const updatedItems = workFlowItems.map((item) =>
              item[id] === data[id] ? { ...item, ...data } : item
            );
            setWorkFlowItems(updatedItems);
            setEditDocTemplate();
          }}
          isEdit={editDocTemplate}
          component={"WORK_FLOW"}
        />
      )}
      {editClientInterview && (
        <AddEditClientInterview
          matterId={props.matterId}
          cancelEdit={() => {
            setEditClientInterview();
          }}
          updateTemplates={(data) => {
            const id = "clientInterviewId";
            const updatedItems = workFlowItems.map((item) =>
              item[id] === data[id] ? { ...item, ...data } : item
            );
            setWorkFlowItems(updatedItems);
            setEditClientInterview();
          }}
          isEdit={editClientInterview}
          component={"WORK_FLOW"}
        />
      )}
    </Dialog>
  );
};

export default WorkFlowDialog;

const WorkFlowItem = ({
  item,
  index,
  onRemove,
  editItem,
  icon,
  detailsKey,
}) => {
  return (
    <div
      className={`row ${styles["items-list"]} d-flex align-items-center`}
      style={{
        backgroundColor: index % 2 === 0 && "#F9F9FF",
      }}
    >
      <div className="d-flex col-5 gap-3 align-items-center">
        <span style={{ cursor: "move" }} data-drag-handle="true">
          <SvgIcon
            style={{
              pointerEvents: "none",
              height: "20px",
              width: "20px",
            }}
            icon={reorderIcon}
          />
        </span>
        <img
          style={{
            pointerEvents: "none",
            height: "20px",
            width: "20px",
          }}
          src={icon}
        />
        <div
          className={`text-truncate ${styles["question-text"]}`}
          style={{
            fontSize: "14px",
          }}
        >
          {item?.name}
        </div>
      </div>

      {/* <div className={`col-4 text-truncate ${styles["question-text"]}`}>
        {item?.[nameKey]}
      </div> */}

      <div className={`col-5 text-truncate ${styles["question-text"]}`}>
        {item?.[detailsKey]}
      </div>

      <div className="d-flex col-2 gap-2 justify-content-end">
        <div>
          <img
            className="cursor-pointer"
            style={{ width: "18px", height: "18px" }}
            src={EditIcon}
            onClick={() => editItem(item)}
            alt="Edit Icon"
          ></img>
        </div>
        <MdCancel
          color="red"
          className="cursor-pointer"
          size={20}
          onClick={() => {
            const id =
              item?.userScreenId ||
              item?.clientInterviewId ||
              item?.documentTemplateId;

            onRemove(id, item.type);
          }}
        />
      </div>
    </div>
  );
};

const UserScreenWorkFlowItem = (props) => (
  <WorkFlowItem
    {...props}
    title="User Screen"
    icon={UserScreenIcon}
    nameKey="name"
    detailsKey="details"
    editItem={props.editDocument}
  />
);

const UserDocumentFlowItem = (props) => (
  <WorkFlowItem
    {...props}
    title={props.editDocument?.name}
    icon={props?.item?.itemType === "SubDocument" ? SubDocument : Document}
    nameKey="templateName"
    detailsKey="details"
    editItem={props.editDocument}
  />
);

const UserClientInterviewFlowItem = (props) => (
  <WorkFlowItem
    {...props}
    title={props.editClientInterview?.name}
    icon={Interview}
    nameKey="processingFileName"
    detailsKey="details"
    editItem={props.editClientInterview}
  />
);
